import React, { FunctionComponent, useState } from "react"
import { useTranslation } from "react-i18next"

import { Flex } from "components/Flex"

import Button, { ButtonTheme } from "components/Button/Button"
import Modal, { IModalProps } from "components/Modal/Modal"

export interface IConfirmModalProps extends IModalProps {
    onConfirm?(close?: IModalProps['onClose']): Promise<void> | void
    theme?: ButtonTheme
    isBusy?: boolean
    cancelButtonLabel?: string
    confirmButtonLabel?: string
}

const ConfirmModal: FunctionComponent<React.PropsWithChildren<IConfirmModalProps>> = ({
    onConfirm,
    onClose,
    theme = "primary",
    cancelButtonLabel,
    confirmButtonLabel,
    isBusy = false,
    children,
    ...others
}) => {
    const { t } = useTranslation()
    const [isConfirming, setConfirming] = useState(false)
    const handleConfirm = async () => {
        try {
            setConfirming(true)
            await onConfirm?.(onClose)
        } finally {
            setConfirming(false)
        }
    }

    return (
        <Modal
            onClose={isBusy ? undefined : onClose}
            footer={
                <Flex justifyContent="flex-end">
                    {!!onClose &&
                        <Button
                            theme="secondary"
                            label={cancelButtonLabel || t('common.cancel')}
                            width="auto"
                            onClick={onClose}
                            disabled={isBusy}
                            data-testid="confirm-modal-cancel" />
                    }
                    <Button
                        theme={theme}
                        label={confirmButtonLabel || t('common.continue')}
                        onClick={onConfirm ? () => handleConfirm() : undefined}
                        width="auto"
                        className="ml-2"
                        disabled={!onConfirm || isBusy || isConfirming}
                        loading={isBusy || isConfirming}
                        data-testid="confirm-modal-continue" />
                </Flex>
            }
            {...others}>
            {children}
        </Modal>
    )
}

export default ConfirmModal
